import { uniq } from 'lodash-es'
import type { AppModule, Service, ServiceModule, ServiceModuleOptions, ServicesType } from '@/config/types'
import { bindService, dynamicClassDependsSymbol, getContainer } from '@/core/container/container'
import { getSymbolFor } from '@/utils/app'
import TmLogicError from '@/core/error/tmLogicError'
import { regisiterInServiceManager } from '@/core/middlewares/middleware'
import { idleTask } from '@/utils/idleTask'
import type { Dict } from '@/types'
import { Queue } from '@/utils/queue'
import { isTest, isAppModuleDebug } from '@/utils/system'
import { TmBaseError } from '@/core/error/tmBaseError'
import type { RegisterServicesForModuleHooks } from '@/core/container/deps.types'
import { moduleDebugger } from '@textmagic/vite-plugin-devtools-appmodule'
import { serviceToModuleMap } from '@/core/container/serviceModuleRegistry'

// not loaded and not registered in the IoC dependencies
const servicesDeclaration = new Map<string, Service>()
// queue of deps to registration in the IoC
const servicesToBindQueue = new Queue<Service>('id')
// deps are not registered in the IoC dependencies
const unboundDeps = new Map<string, Service>()
// not loaded modules
const modules = new Map<string, ServiceModule>()
// module loader hooks
const moduleLoadedHooks = new Map<string, RegisterServicesForModuleHooks['moduleLoaded']>()
// dependencies are currently downloading and binding in the IoC
const processingDeps = new Map<string, Promise<void>>()
// pending priorityLoad calls — concurrent calls with same routeName+modulesList share the promise
const pendingPriorityLoads = new Map<string, Promise<void>>()
// routes with loaded and resolved dependencies
const resolvedRoutesSet = new Set<string>()
// dependencies are bound to IoC
const boundBackgroundDepsSet = new Set<string>()

// used in background deps loading
const registeredDepsSet = new Set<string>()
const completedBackgroundDepsSet = new Set<string>()
const completedForegroundDepsSet = new Set<string>()
let resolveBindAllDepsFn = (_n: number) => {}
let rejectBindAllDepsFn = (_e: Error) => {}
let callIdleTaskCounter = 0
let isIdleTaskRunning = false
// to VUE_APP_SERVICES_IN_MODULES_WARNING = true
const servicesInModules: Record<string, { module: string; from: Set<string> }> = {}

export const getNotLoadedModules = (): Dict<ServiceModule> => {
  const result: Dict<ServiceModule> = {}
  for (const [name, srv] of modules.entries()) {
    if (srv.promise instanceof Promise) {
      continue
    }
    result[name] = srv
  }
  return result
}

export const loadModules = async (
  moduleNames: string[],
  options?: { triggeredBy?: 'buildContainer' | 'priorityLoad' | 'bindDep' | 'background'; routeName?: string },
) => {
  const promises: Array<Promise<unknown>> = []
  moduleNames = uniq(moduleNames)

  for (const name of moduleNames) {
    const module = modules.get(name)
    if (!module) {
      continue
    }
    if (module.promise instanceof Promise) {
      promises.push(module.promise)
    } else {
      // #region Related to vite-plugin-devtools-appmodule
      if (isAppModuleDebug && options?.triggeredBy) {
        moduleDebugger.emit({
          type: 'module:load:start',
          moduleName: name as AppModule,
          triggeredBy: options.triggeredBy,
          routeName: options.routeName,
          time: Date.now(),
        })
      }
      // #endregion

      // this is necessary to avoid double loading of module
      // on rejection, clear cached promise so a retry can re-invoke the loader
      module.promise = module.loader().catch((e) => {
        module.promise = undefined
        throw e
      })
      promises.push(module.promise)
    }
  }
  await Promise.all(promises)
  // #region Related to vite-plugin-devtools-appmodule
  if (isAppModuleDebug) {
    for (const name of moduleNames) {
      moduleDebugger.emit({ type: 'module:load:end', moduleName: name as AppModule, time: Date.now() })
    }
  }
  // #endregion
  for (const name of moduleNames) {
    modules.delete(name)
  }
}

const getServiceMatcher =
  (routeName?: string, moduleNames?: readonly string[]) =>
  (srv: Service): boolean =>
    Boolean(srv.routeName === routeName || (srv.module && moduleNames?.includes(srv.module)))

export const getServicesIterator = (routeName?: string, moduleNames?: AppModule[]): IterableIterator<Service> => {
  return servicesToBindQueue.cutSubQueue(getServiceMatcher(routeName, moduleNames))
}

// A pass either binds something or drains something in flight, so a handful is always enough.
const MAX_BIND_PASSES = 50

/**
 * Binds every matching dependency. unboundDeps is the registry of what is registered but not
 * bound; the bind queue is only a work list that the background drain empties, so it cannot
 * answer what is left.
 */
const bindMatchedDeps = async (matches: (srv: Service) => boolean) => {
  for (let pass = 0; pass < MAX_BIND_PASSES; pass += 1) {
    let progressed = false

    for (const srv of [...unboundDeps.values()]) {
      if (!matches(srv)) {
        continue
      }

      servicesToBindQueue.remove(srv)
      // eslint-disable-next-line no-await-in-loop
      await bindDep(srv)
      progressed = true
    }

    if (!progressed) {
      return
    }
  }
  throw new TmLogicError(`Dependencies are still not bound after ${MAX_BIND_PASSES} passes`)
}

/**
 * Loading and binding into IoC all startup dependencies
 * @returns
 */
export const loadAndBindStartupDeps = async () => {
  const foregroundModules: AppModule[] = []
  for (const [name, module] of modules.entries()) {
    if (module.options?.startup) {
      if (!module.options.background) {
        foregroundModules.push(name as AppModule)
      }
    }
  }
  if (foregroundModules.length) {
    return priorityLoadAndBindAllDepsForModules('', foregroundModules)
  }
  return Promise.resolve()
}

/**
 * Loading and binding into IoC all deferred dependencies
 * @returns
 */
export const loadAndBindDeferredDeps = async () => {
  const deferredModules: AppModule[] = []
  for (const [name, module] of modules.entries()) {
    if (module.options?.deferred) {
      deferredModules.push(name as AppModule)
    }
  }
  if (deferredModules.length) {
    return priorityLoadAndBindAllDepsForModules('', deferredModules)
  }
  return Promise.resolve()
}

/**
 * Loading and binding into IoC all dependencies in the BACKGROUND
 * @returns
 */
export const backgroundLoadAndBindAllDeps = async () => {
  return new Promise<number>((resolve, reject) => {
    resolveBindAllDepsFn = resolve
    rejectBindAllDepsFn = reject
    idleTask(modules.entries(), async ([name, module]) => {
      if (!module.promise) {
        if (isAppModuleDebug) {
          moduleDebugger.emit({
            type: 'module:load:start',
            moduleName: name as AppModule,
            triggeredBy: 'background',
            time: Date.now(),
          })
        }
        const loading = module.loader().then((result) => {
          if (isAppModuleDebug) {
            moduleDebugger.emit({ type: 'module:load:end', moduleName: name as AppModule, time: Date.now() })
          }
          return result
        })
        module.promise = loading
        // Clears the cached promise so a failed load can be retried, and keeps a background
        // failure from becoming an unhandled rejection. loadModules still sees the rejection.
        loading.catch(() => {
          if (module.promise === loading) {
            module.promise = undefined
          }
        })
      }
    }).catch((e) => {
      reject(e)
    })
  })
}

/**
 * Loading and binding specific dependencies in IoC in FOREGROUND
 * @param routeName route name (optional)
 * @param modulesList array of module names (optional)
 */
export const priorityLoadAndBindAllDepsForModules = async (routeName: string = '', modulesList: AppModule[] = []) => {
  const compositeKey = `${routeName}::${[...modulesList].sort().join(',')}`
  const existing = pendingPriorityLoads.get(compositeKey)
  if (existing) return existing

  const promise = (async () => {
    try {
      if (Array.isArray(modulesList) && modulesList.length) {
        await loadModules(modulesList, { triggeredBy: 'priorityLoad', routeName })
      }
      await bindMatchedDeps(getServiceMatcher(routeName, modulesList))
    } finally {
      pendingPriorityLoads.delete(compositeKey)
    }
  })()
  pendingPriorityLoads.set(compositeKey, promise)
  return promise
}

export const priorityLoadAndBindDeps = async (deps: string[]) => {
  const modulesList = getModulesForDeps(deps)
  if (modulesList.length) {
    await loadModules(modulesList)
  }
  await bindMatchedDeps((srv) => deps.includes(srv.id!))
}

export const registerServicesForModule = async (
  name: AppModule,
  services: ServicesType<string>,
  hooks?: RegisterServicesForModuleHooks,
) => {
  if (typeof hooks?.moduleLoaded === 'function') {
    moduleLoadedHooks.set(name, hooks.moduleLoaded)
  }

  Object.keys(services).forEach((id: string) => {
    const srv = servicesDeclaration.get(id)
    if (srv) {
      servicesDeclaration.delete(id)
      Object.assign(srv, services[id])
      srv.id = id
      srv.loader = () => services[id].bindingValue
      srv.module = name
      servicesToBindQueue.add(srv)
      registeredDepsSet.add(id)
      unboundDeps.set(id, srv)
    } else {
      throw new TmLogicError(`Service with the id=${id} from module ${name} was not found in the module declaration`)
    }
  })
  modules.delete(name)
  if (isTest) {
    // we don't need to load dependencies in the background for unit tests
    return
  }
  try {
    if (!isIdleTaskRunning) {
      callIdleTaskCounter += 1
      isIdleTaskRunning = true
      let errorCounter = 0
      await idleTask(
        servicesToBindQueue,
        (srv: Service) => {
          // we can do it synchronously for background loading
          bindDep(srv, false).then(() => {
            if (boundBackgroundDepsSet.has(srv.id!)) {
              completedBackgroundDepsSet.add(srv.id!)
            }
          })
        },
        (e: Error, srv: Service) => {
          errorCounter += 1
          if (errorCounter > 10) {
            throw e
          }
          servicesToBindQueue.add(srv)
        },
      )

      isIdleTaskRunning = false
      callIdleTaskCounter -= 1
      if (!modules.size && !callIdleTaskCounter) {
        const allSrvCount = servicesDeclaration.size + registeredDepsSet.size
        if (
          allSrvCount === completedBackgroundDepsSet.size ||
          allSrvCount === completedBackgroundDepsSet.size + completedForegroundDepsSet.size
        ) {
          resolveBindAllDepsFn(completedBackgroundDepsSet.size)
        } else {
          const unregisteredDeps = [...registeredDepsSet].filter((service) => getContainer().isBound(service))

          if (unregisteredDeps.length) {
            rejectBindAllDepsFn(
              new TmLogicError(
                'Background services loading: these services were registered from modules but not registered in middlewares: ' +
                  unregisteredDeps.join(','),
              ),
            )
          }
        }
        registeredDepsSet.clear()
        boundBackgroundDepsSet.clear()
        completedBackgroundDepsSet.clear()
        completedForegroundDepsSet.clear()
      }
    }
  } catch (e) {
    rejectBindAllDepsFn(e as Error)
  }
}

function getDepsIdentifiers(target: object, withOptional: boolean = false): symbol[] {
  const CLASS_METADATA_KEY = '@inversifyjs/core/classMetadataReflectKey'
  const metadata = Reflect.getMetadata(CLASS_METADATA_KEY, target)
  if (!metadata) {
    return []
  }
  const ids = new Set<unknown>()
  // Add dependencies from constructor
  if (metadata.constructorArguments) {
    for (const arg of metadata.constructorArguments) {
      if (arg?.value && (!arg.optional || withOptional)) {
        // Exclude optional
        ids.add(arg.value)
      }
    }
  }
  // Add dependencies from properties
  if (metadata.properties) {
    for (const [, propertyMetadata] of metadata.properties) {
      if (propertyMetadata?.value && (!propertyMetadata.optional || withOptional)) {
        // Exclude optional
        ids.add(propertyMetadata.value)
      }
    }
  }
  return Array.from(ids) as symbol[]
}

const getDynamicClassDependencies = (bindingValue: () => unknown): symbol[] => {
  if (!bindingValue[dynamicClassDependsSymbol]) {
    throw new TmLogicError('Not set dependencies for dynamic class')
  }
  return bindingValue[dynamicClassDependsSymbol].map((dep) => dep.serviceIdentifier)
}

export const checkDeps = (key: string, service: Service): AppModule[] => {
  if (service.bindingType === 'value') {
    return []
  }
  let depIds: symbol[] = []
  try {
    depIds =
      service.bindingType === 'dynamicClass'
        ? getDynamicClassDependencies(service.bindingValue)
        : getDepsIdentifiers(service.bindingValue, true)
  } catch (e) {
    if ((e as Error).message?.indexOf('Missing required @inject') >= 0) {
      return []
    }
  }
  const result = new Set<AppModule>()
  for (let i = 0; i < depIds.length; i++) {
    const depId = depIds[i].description as string
    const srv = servicesDeclaration.get(depId)
    if (srv?.module) {
      result.add(srv.module)
      if (import.meta.env.VUE_APP_SERVICES_IN_MODULES_WARNING === 'true') {
        let record = servicesInModules[depId]
        if (!record) {
          record = { module: srv.module, from: new Set() }
        }
        record.from.add(key)
        servicesInModules[depId] = record
      }
    }
  }

  // #region Related to vite-plugin-devtools-appmodule
  if (isAppModuleDebug) {
    for (const mod of result) {
      moduleDebugger.emit({
        type: 'module:required',
        serviceName: key,
        moduleName: mod,
        deps: depIds
          .filter((id) => servicesDeclaration.get(id.description as string)?.module === mod)
          .map((id) => id.description as string),
        time: Date.now(),
      })
    }
  }
  // #endregion

  return Array.from(result)
}

export const getModulesForDeps = (deps: string[]): AppModule[] => {
  const result = new Set<AppModule>()
  for (const dep of deps) {
    const srv = servicesDeclaration.get(dep)
    if (srv?.module) {
      result.add(srv.module)
    }
  }
  return Array.from(result)
}

export const printServicesInModules = () => {
  const obj = {}
  Object.keys(servicesInModules).forEach((key) => {
    obj[key] = { module: servicesInModules[key].module, services: Array.from(servicesInModules[key].from).join(',') }
  })
  // eslint-disable-next-line no-console
  console.table(obj)
}

const loadAndBindDep = async (service: Service, foreground: boolean) => {
  if (service.module) {
    const module = modules.get(service.module)
    if (module?.promise) {
      await module.promise
    } else if (module) {
      // #region Related to vite-plugin-devtools-appmodule
      if (isAppModuleDebug) {
        moduleDebugger.emit({
          type: 'module:load:start',
          moduleName: service.module as AppModule,
          triggeredBy: 'bindDep',
          time: Date.now(),
        })
      }
      // #endregion

      await loadModules([service.module])
    }
  }
  if (typeof service.bindingValue !== 'function') {
    // service.bindingValue is not constructor
    if (!service.loader) {
      throw new TmLogicError(`Dependency with id=${service.id!.toString()} must have a loader function`)
    }
    const res = await service.loader()
    service.bindingValue = res.default
  }
  let depIds: symbol[] = []
  try {
    depIds =
      service.bindingType === 'dynamicClass'
        ? getDynamicClassDependencies(service.bindingValue)
        : getDepsIdentifiers(service.bindingValue, true)
  } catch (e) {
    if (e instanceof Error) {
      if (typeof service.id === 'string') {
        throw new Error(`An error occurred when trying to bind the "${service.id}" service: ${e.message}`)
      } else {
        throw new Error(`An error occurred when trying to bind the untitled dependency: ${e.message}`)
      }
    } else {
      throw e
    }
  }

  const deps = depIds
    .map((depId) => {
      if (getContainer().isBound(depId)) {
        return undefined
      }
      const srv = unboundDeps.get(depId.description!) ?? servicesDeclaration.get(depId.description!)

      // #region Related to vite-plugin-devtools-appmodule
      if (srv) {
        if (isAppModuleDebug && srv.module) {
          moduleDebugger.emit({
            type: 'service:dep:module',
            serviceName: service.id!,
            depName: depId.description as string,
            moduleName: srv.module as AppModule,
            fromModule: service.module,
            time: Date.now(),
          })
        }
      }
      // #endregion

      return srv
    })
    .filter((dep) => !!dep)

  if (!foreground && deps.length) {
    servicesToBindQueue.remove(service)

    deps.forEach((dep) => {
      servicesToBindQueue.add(dep)
    })

    servicesToBindQueue.add(service)

    return
  }

  for (const dep of deps) {
    // eslint-disable-next-line no-await-in-loop
    await bindDep(dep)
  }

  bindService(service.id!, service)
  unboundDeps.delete(service.id!)
  // Part of binding, not a follow-up: a service is only reachable through its manager after this
  regisiterInServiceManager(service)
  if (foreground) {
    completedForegroundDepsSet.add(service.id!)
  } else {
    boundBackgroundDepsSet.add(service.id!)
  }
}

export const bindDep = async (service: Service, foreground = true) => {
  if (!service) {
    return
  }
  const processingPromise = processingDeps.get(service.id!)
  if (processingPromise) {
    if (foreground) {
      await processingPromise
    } else {
      return
    }
  }
  if (getContainer().isBound(getSymbolFor(service.id!))) {
    // Keep unboundDeps honest: the loader and the route guard both trust it
    unboundDeps.delete(service.id!)

    return
  }
  let resolveFunc!: () => void
  processingDeps.set(
    service.id!,
    new Promise<void>((resolve) => {
      resolveFunc = resolve
    }),
  )
  try {
    await loadAndBindDep(service, foreground)
  } finally {
    // A failed binding must release waiters and leave no entry behind, or a later attempt either
    // waits forever on it or gives up without binding
    resolveFunc()
    processingDeps.delete(service.id!)
  }
}

export const registerDepsForRoute = (services: Service[], routeName: string): Record<symbol, Service> => {
  const result: Record<symbol, Service> = {}
  services.forEach((srv) => {
    srv.routeName = routeName
    srv.id = srv.bindingValue
    servicesToBindQueue.add(srv)
    registeredDepsSet.add(typeof srv.id === 'symbol' ? (srv.id as symbol).description! : String(srv.id))
    result[srv.bindingValue] = srv
  })
  return result
}

export const registerModuleDeclaration = (
  name: AppModule,
  loader: () => Promise<unknown>,
  services: Record<string, symbol>,
  options?: ServiceModuleOptions,
): Record<symbol, Service> => {
  modules.set(name, {
    loader: () => {
      return loader().then(() => {
        if (moduleLoadedHooks.has(name)) {
          moduleLoadedHooks.get(name)!(name)
        }
      })
    },
    services,
    options,
  })
  Object.keys(services).forEach((type: string) => {
    const srv: Service = {
      id: type,
      module: name,
      bindingValue: type,
    }
    servicesDeclaration.set(type, srv)
    serviceToModuleMap.set(type, name)
  })
  return services
}

export const isModulesAreLoaded = (moduleNames: string[]) => {
  let result = true
  for (const name of moduleNames) {
    const module = modules.get(name)
    if (module && !module.promise) {
      result = false
      break
    }
  }
  return result
}

export const isAllDepsAreResolved = (routeName: string, moduleNames: string[] = []) => {
  if (resolvedRoutesSet.has(routeName)) {
    return true
  }
  // Every place a dependency can be waiting: module not loaded, registered but unbound, queued
  const matches = getServiceMatcher(routeName, moduleNames)
  for (const srv of servicesDeclaration.values()) {
    if (matches(srv)) {
      return false
    }
  }
  for (const srv of unboundDeps.values()) {
    if (matches(srv)) {
      return false
    }
  }
  if (servicesToBindQueue.filter(matches).length) {
    return false
  }
  resolvedRoutesSet.add(routeName)
  return true
}

export const getModulesMap = () => {
  if (!isTest) {
    throw new TmBaseError('Allowed only in tests')
  }
  return modules
}
